// import {
//   useEffect,
//   useCallback,
//   useRef,
//   useMemo,
//   type CSSProperties,
// } from "react";
// import { preload } from "react-dom";
// import { useAppDispatch, useAppSelector } from "@/store/hooks";
// import {
//   selectAdminBackgroundUrl,
//   selectAdminIsImageLoaded,
//   setAdminBackgroundUrl,
//   setAdminImageLoaded,
// } from "@/store/slices/adminBackgroundSlice/adminBackgroundSlice";

// import {
//   ADMIN_DEFAULT_BACKGROUND_URL,
//   ADMIN_OVERLAY_URL,
//   ADMIN_FALLBACK_COLOR,
//   ADMIN_BG_OPACITY,
//   ADMIN_OVERLAY_OPACITY,
//   ADMIN_TRANSITION_DURATION,
// } from "@/layouts/AdminBackgroundLayout/AdminBackgroundLayout.types";
// import { logger } from "../utils";
// import { useApiQuery } from "../api/hooks";
// import type { IImages } from "@/features/users/types";

// interface UseAdminBackroundReturn {
//   /* Css variables to apply to layout container. Apply via: style={backgroundStyles}  */
//   backgroundStyles: CSSProperties;

//   backgroundImageUrl: string | null;
//   effectiveUrl: string;
//   fallbackColor: string;
//   isImageLoaded: boolean;
//   isFetching: boolean;
//   isUploading: boolean;
//   isRemoving: boolean;
//   isUsingCustomBackground: boolean;
//   upload: (file: File) => Promise<void>;
//   remove: () => Promise<void>;
// }

// export const useAdminBackground = (): UseAdminBackroundReturn => {
//   const dispatch = useAppDispatch();
//   const imageRefFromServer = useRef<string | null>(null);
//   const imageRef = useRef<HTMLImageElement | null>(null);
//   const {
//     data: imageData,
//     isLoading,
//     isError,
//     error,
//     parsedError,
//     isSuccess,
//   } = useApiQuery<IImages>({
//     queryKey: ["bgImage"],
//     url: "/settings/bg-image",
//   });

//   useEffect(() => {
//     logger.info("Image Data", imageData!);
//   }, [imageData]);
//   // Redux
//   // const customUrl = useAppSelector(selectAdminBackgroundUrl);
//   const customUrl = imageData?.url;
//   const isImageLoaded = useAppSelector(selectAdminIsImageLoaded);

//   // Effective Url ( custom or default)
//   const effectiveUrl = customUrl ?? ADMIN_DEFAULT_BACKGROUND_URL;

//   // React Query
//   const isFetching = false; // here we call the useAdminBackgroundSettings();
//   const uploadMutation = null; //useUploadAdminBackground();
//   const removeMutation = null; //useRemoveAdminBackground();

//   // LazyLoad Image
//   useEffect(() => {
//     if (!effectiveUrl) return;
//     // Cleanup previous image
//     if (imageRef.current) {
//       imageRef.current.onload = null;
//       imageRef.current.onerror = null;
//       imageRef.current.src = "";
//       imageRef.current = null;
//     }
//     // Reset loaded state when url changes
//     dispatch(setAdminImageLoaded(false));

//     // Preload Reacr 19 preload for resource hints
//     preload(effectiveUrl, {
//       as: "image",
//       fetchPriority: "high",
//       crossOrigin: "anonymous",
//     });
//     // Create image element to track loading
//     const img = new Image();
//     imageRef.current = img;
//     img.crossOrigin = "anonymous";

//     img.onload = () => dispatch(setAdminImageLoaded(true));
//     img.onerror = () => dispatch(setAdminImageLoaded(false));
//     // start loading
//     img.src = effectiveUrl;

//     // Cleanup unmount or URL change
//     return () => {
//       if (imageRef.current) {
//         imageRef.current.onload = null;
//         imageRef.current.onerror = null;
//         imageRef.current.src = "";
//         imageRef.current = null;
//       }
//     };
//   }, [effectiveUrl, dispatch]);

//   // ================================================================
//   // Css variables for layout
//   // ================================================================
//   const backgroundStyles = useMemo(
//     (): CSSProperties =>
//       ({
//         // Fallback color (always visible as base layer)
//         "--admin-fallback-color": ADMIN_FALLBACK_COLOR,
//         //Primary background image (::before)
//         "--admin-bg-image": isImageLoaded ? `url(${effectiveUrl})` : "none",
//         "--admin-bg-opacity": isImageLoaded ? ADMIN_BG_OPACITY : 0,
//         // Secondary overlay image (::after) - always static
//         "--admin-overlay-image": `url(${ADMIN_OVERLAY_URL})`,
//         "--admin-overlay-opacity": ADMIN_OVERLAY_OPACITY,
//         "--admin-transition-duration": `${ADMIN_TRANSITION_DURATION}`,
//       }) as CSSProperties,
//     [effectiveUrl, isImageLoaded],
//   );
//   const upload = useCallback(
//     async (file: File) => {
//       return Promise.reject();
//       logger.info("file", file);
//       // await uploadMutation!.mutateAsync(file);
//     },
//     [uploadMutation],
//   );

//   const remove = useCallback(async () => {
//     return Promise.reject();
//     //await removeMutation.mutateAsync();
//   }, [removeMutation]);

//   return {
//     backgroundStyles,
//     backgroundImageUrl: isImageLoaded ? effectiveUrl : null,
//     effectiveUrl,
//     fallbackColor: ADMIN_FALLBACK_COLOR,
//     isImageLoaded,
//     isFetching,
//     isUploading: false,
//     isRemoving: false,
//     isUsingCustomBackground: customUrl !== null,
//     upload,
//     remove,
//   };
// };

import {
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
} from "react";
import { preload } from "react-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectAdminIsImageLoaded,
  setAdminBackgroundUrl,
  setAdminImageLoaded,
} from "@/store/slices/adminBackgroundSlice/adminBackgroundSlice";
import {
  ADMIN_DEFAULT_BACKGROUND_URL,
  ADMIN_OVERLAY_URL,
  ADMIN_FALLBACK_COLOR,
  ADMIN_BG_OPACITY,
  ADMIN_OVERLAY_OPACITY,
  ADMIN_TRANSITION_DURATION,
} from "@/layouts/AdminBackgroundLayout/AdminBackgroundLayout.types";
import { logger } from "../utils";
import { useApiQuery } from "../api/hooks";

interface UseAdminBackgroundReturn {
  backgroundStyles: CSSProperties;
  backgroundImageUrl: string | null;
  effectiveUrl: string;
  fallbackColor: string;
  isImageLoaded: boolean;
  isFetching: boolean;
  isUploading: boolean;
  isRemoving: boolean;
  isUsingCustomBackground: boolean;
  upload: (file: File) => Promise<void>;
  remove: () => Promise<void>;
}

// ✅ Helper: Validate URL is a real, usable URL
const isValidUrl = (url: unknown): url is string => {
  if (typeof url !== "string") return false;
  if (url.trim() === "") return false;
  return true;
};

export const useAdminBackground = (): UseAdminBackgroundReturn => {
  const dispatch = useAppDispatch();
  const imageRef = useRef<HTMLImageElement | null>(null);

  const { data, isLoading: isFetching } = useApiQuery<{
    imageData: {
      publicId: string;
      url: string;
      secureUrl: string;
      format: string;
      width: number;
      height: number;
      size: number;
    };
  }>({
    queryKey: ["bgImage"],
    url: "/settings/bg-image",
  });

  logger.info("imageData", data?.imageData.url);
  // ✅ FIX 1: Extract URL safely with proper validation
  const customUrl = isValidUrl(data?.imageData?.url)
    ? data?.imageData?.url
    : null;

  const isImageLoaded = useAppSelector(selectAdminIsImageLoaded);

  // ✅ FIX 2: Only compute effectiveUrl AFTER API has resolved
  // While loading → use default
  // After loading → use custom if available, otherwise default
  const effectiveUrl = useMemo(() => {
    // API is still loading, don't decide yet - use default as placeholder
    if (isFetching) {
      logger.info("⏳ API still loading, using default background");
      return ADMIN_DEFAULT_BACKGROUND_URL;
    }

    // API resolved with a valid URL
    if (customUrl) {
      logger.info("🖼️ Using custom background from server:", customUrl);
      return customUrl;
    }

    // API resolved but no custom URL (or error)
    logger.info("🏔️ No custom background, using default");
    return ADMIN_DEFAULT_BACKGROUND_URL;
  }, [customUrl, isFetching]);

  // ✅ FIX 3: Sync to Redux when API data changes
  useEffect(() => {
    if (customUrl) {
      dispatch(setAdminBackgroundUrl(customUrl));
    }
  }, [customUrl, dispatch]);

  // ✅ FIX 4: Load image - only when effectiveUrl changes AND API is done
  useEffect(() => {
    // Don't start loading while API is still fetching
    // This prevents loading DEFAULT first, then SERVER image
    if (isFetching) return;
    if (!effectiveUrl) return;

    // Cleanup previous image
    if (imageRef.current) {
      imageRef.current.onload = null;
      imageRef.current.onerror = null;
      imageRef.current.src = "";
      imageRef.current = null;
    }

    // Reset loaded state
    dispatch(setAdminImageLoaded(false));

    // Preload with React 19
    preload(effectiveUrl, {
      as: "image",
      fetchPriority: "high",
      crossOrigin: "anonymous",
    });

    // Track actual loading
    const img = new Image();
    imageRef.current = img;
    img.crossOrigin = "anonymous";

    img.onload = () => {
      logger.info("✅ Image loaded:", effectiveUrl);
      dispatch(setAdminImageLoaded(true));
    };

    img.onerror = (err) => {
      logger.error("❌ Image failed to load:", effectiveUrl, err);
      dispatch(setAdminImageLoaded(false));

      // ✅ FIX 5: Fallback to default if custom image fails
      if (effectiveUrl !== ADMIN_DEFAULT_BACKGROUND_URL) {
        logger.info("🔄 Falling back to default background");
        dispatch(setAdminBackgroundUrl(null));
      }
    };

    img.src = effectiveUrl;

    return () => {
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
        imageRef.current.src = "";
        imageRef.current = null;
      }
    };
  }, [effectiveUrl, isFetching, dispatch]);

  // CSS variables
  const backgroundStyles = useMemo(
    (): CSSProperties =>
      ({
        "--admin-fallback-color": ADMIN_FALLBACK_COLOR,
        "--admin-bg-image": isImageLoaded ? `url(${effectiveUrl})` : "none",
        "--admin-bg-opacity": isImageLoaded ? ADMIN_BG_OPACITY : 0,
        "--admin-overlay-image": `url(${ADMIN_OVERLAY_URL})`,
        "--admin-overlay-opacity": ADMIN_OVERLAY_OPACITY,
        "--admin-transition-duration": `${ADMIN_TRANSITION_DURATION}`,
      }) as CSSProperties,
    [effectiveUrl, isImageLoaded],
  );

  const upload = useCallback(async (file: File): Promise<void> => {
    logger.info("Uploading file:", file.name);
    throw new Error("Upload not implemented yet");
  }, []);

  const remove = useCallback(async (): Promise<void> => {
    throw new Error("Remove not implemented yet");
  }, []);

  return {
    backgroundStyles,
    backgroundImageUrl: isImageLoaded ? effectiveUrl : null,
    effectiveUrl,
    fallbackColor: ADMIN_FALLBACK_COLOR,
    isImageLoaded,
    isFetching,
    isUploading: false,
    isRemoving: false,
    isUsingCustomBackground: customUrl !== null,
    upload,
    remove,
  };
};
